import { createInstance, type i18n, type ResourceLanguage } from "i18next";
import type {} from "./types.js";
import common from "./catalogs/en/common.js";
import web from "./catalogs/en/web.js";
import errors from "./catalogs/en/errors.js";
import { pseudoCatalog } from "./pseudo.js";
import type { Locale, LocaleEnvironment, PreviewLocale } from "./locales.js";
import { enabledLocales } from "./locales.js";

export const englishWebResources = { common, web, errors };

// Explicit imports keep Vite's browser chunks separate from bot/email catalogs.
const webLoaders = {
  ru: () => Promise.all([import("./catalogs/ru/common.js"), import("./catalogs/ru/web.js"), import("./catalogs/ru/errors.js")]),
  es: () => Promise.all([import("./catalogs/es/common.js"), import("./catalogs/es/web.js"), import("./catalogs/es/errors.js")]),
  de: () => Promise.all([import("./catalogs/de/common.js"), import("./catalogs/de/web.js"), import("./catalogs/de/errors.js")]),
  fr: () => Promise.all([import("./catalogs/fr/common.js"), import("./catalogs/fr/web.js"), import("./catalogs/fr/errors.js")]),
  it: () => Promise.all([import("./catalogs/it/common.js"), import("./catalogs/it/web.js"), import("./catalogs/it/errors.js")]),
  "pt-PT": () => Promise.all([import("./catalogs/pt-PT/common.js"), import("./catalogs/pt-PT/web.js"), import("./catalogs/pt-PT/errors.js")]),
} satisfies Record<Exclude<Locale, "en">, () => Promise<readonly { default: Record<string, string> }[]>>;

export function createWebI18n(): i18n {
  const instance = createInstance();
  // Embedded English is available synchronously, even offline. All other
  // resources load before changeLanguage; consumers never need Suspense.
  void instance.init({
    lng: "en", fallbackLng: "en", defaultNS: "common", keySeparator: false,
    ns: ["common", "web", "errors"], returnNull: false, initAsync: false,
    resources: { en: englishWebResources }, interpolation: { escapeValue: false },
  });
  return instance;
}

const loads = new WeakMap<i18n, Map<PreviewLocale, Promise<void>>>();

export async function loadWebLocale(instance: i18n, locale: PreviewLocale, environment: LocaleEnvironment): Promise<void> {
  if (locale === "en-XA" ? environment === "production" : !enabledLocales(environment).includes(locale)) {
    throw new Error(`locale is not enabled: ${locale}`);
  }
  if (instance.hasResourceBundle(locale, "web")) return;
  let pending = loads.get(instance);
  if (!pending) loads.set(instance, pending = new Map());
  const existing = pending.get(locale);
  if (existing) return existing;
  const promise = (async () => {
    let resources: ResourceLanguage;
    if (locale === "en-XA") {
      resources = Object.fromEntries(Object.entries(englishWebResources).map(([namespace, catalog]) => [namespace, pseudoCatalog(catalog)]));
    } else if (locale !== "en") {
      const [common, web, errors] = await webLoaders[locale]();
      resources = { common: common.default, web: web.default, errors: errors.default };
    } else {
      throw new Error(`web catalog is not available: ${locale}`);
    }
    for (const [namespace, catalog] of Object.entries(resources)) {
      instance.addResourceBundle(locale, namespace, catalog);
    }
  })();
  pending.set(locale, promise);
  try { await promise; } finally { pending.delete(locale); }
}
